import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';

import * as service from '../services';
import { Preferences } from '../types';
import { useAderenciaBigNumbers } from './useAderenciaBigNumbers';
import { useProductsByRange } from './useProductsByRange';
import { useAderenciaHistory } from './useAderenciaHistory';
import { useAderenciaPrecosStore } from '../AderenciaPrecos.store';

export const useAderenciaPreference = () => {
  const [aderenciaPreferenceData, setAderenciaPreferenceData] = useState<Preferences>({
    tipoPreco: 'PRECO_PAGO',
    canalFisico: true,
    canalDigital: true,
  });

  const { aderenciaBodyRequest } = useAderenciaPrecosStore();

  const { handleGetAderenciaBigNumbers } = useAderenciaBigNumbers();

  const { handleRequestProductByRange } = useProductsByRange();

  const { handleGetAderenciaHistoryChart } = useAderenciaHistory();

  const savePreference = async (data: Preferences) => {
    const resp = await service.saveAderenciaPreferences(data);
    return resp;
  };

  const getPreference = async () => {
    const res = await service.getAderenciaPreferences();
    return res;
  };

  useQuery({
    queryKey: ['get-aderencia-preference'],
    queryFn: getPreference,
    retry: false,
    onSuccess: data => {
      const preference = data?.data;
      const newData = {
        tipoPreco: preference?.tipoPreco || 'PRECO_PAGO',
        canalFisico: preference?.canalFisico ?? true,
        canalDigital: preference?.canalDigital ?? true,
      };
      setAderenciaPreferenceData(newData);
    },
  });

  const mutate = useMutation({
    mutationFn: savePreference,
    onSuccess: () => {
      handleGetAderenciaBigNumbers(aderenciaBodyRequest);
      handleRequestProductByRange(aderenciaBodyRequest);
      handleGetAderenciaHistoryChart(aderenciaBodyRequest);
    },
  });

  const handleChangeAderenciaPreferences = (name: string, value: string | boolean) => {
    const body = { ...aderenciaPreferenceData, [name]: value };
    setAderenciaPreferenceData(prev => ({ ...prev, [name]: value }));
    mutate.mutateAsync(body);
  };

  const handleChangeCanalPreferences = (name: 'canalFisico' | 'canalDigital', value: boolean) => {
    const body = { ...aderenciaPreferenceData, [name]: value };
    if (body.canalFisico === false && body.canalDigital === false) {
      return;
    }
    setAderenciaPreferenceData(prev => ({ ...prev, [name]: value }));
    mutate.mutateAsync(body);
  };

  return {
    loadingSavePreference: mutate.isPending,
    aderenciaPreferenceData,
    handleChangeAderenciaPreferences,
    handleChangeCanalPreferences,
    handleSaveAderenciaPreference: mutate.mutateAsync,
  };
};
